/**
 * Business-logic layer for OnboardingWizard.
 *
 * All backend interaction lives here; the component (OnboardingWizard.tsx) is
 * pure UI + local form state. It builds an `OnboardingData` snapshot and hands
 * it to `useOnboardingSubmit().submit`. Identity comes from `useOnboardingIdentity`.
 *
 * Verified against the live service swaggers (vessel / service-provider / people
 * / auth). Flows the backend genuinely supports are wired; the rest are marked
 * with `ponytail:` notes explaining why they stay mocked.
 */
import { addDays, addMonths, format } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getServiceId, setServiceId } from '@/api/tokens.helper';
import { generateEmailFromVesselName } from '@/components/CreateSheet/CreateVesselSheet/emailUtils';
import { useUnitPreferences } from '@/hooks/useUnitPreferences';
import { ensureUrlProtocol } from '@/pages/ServiceProviders/utils/ensureUrlProtocol.ts';
import { useAuth } from '@/store/auth/auth.store';
import { createProfile, uploadCv } from '@/store/crew/crew.api';
import { generateCreateCrewProfile } from '@/store/crew/crew.mappers';
import type { WorkType } from '@/store/crew/crew.types';
import { useMarinaServicesStore } from '@/store/marina-services/marina-services';
import type { PersonPreferences } from '@/store/persons/person.dto';
import { usePerson } from '@/store/persons/person.store';
import { addServiceProviderLocation } from '@/store/service-providers/service-providers.api';
import {
  type CreateUpdateServiceProvidersFormValues,
  ServiceProviderCreatedBy,
} from '@/store/service-providers/service-providers.dto';
import { useCreateServiceProvider } from '@/store/service-providers/service-providers.store';
import type { CreateVesselPayload } from '@/store/vessels/vessel.dto';
import { useVessels } from '@/store/vessels/vessels.store';
import { consumeRedirectAfterAuth } from '@/utils/auth-redirect';
import { getBrowserTimeZone } from '@/utils/timezone';
import { toast } from '@/utils/toast';
import { type LengthUnit, toCanonicalFt } from '@/utils/unitConversion';

import type {
  AvailableFrom,
  Form,
  IntentId,
  JobPath,
  PlanId,
  Pref,
  Role,
} from './OnboardingWizard.constants';

/** One geocoded service-provider location (matches backend dto.Location; name required). */
export interface ServiceLocation {
  name: string;
  address: string;
  latitude: number;
  longitude: number;
}

/** Snapshot of wizard state the component passes to `submit`. */
export interface OnboardingData {
  intent: IntentId | null;
  role: Role | null;
  jobPath: JobPath | null;
  form: Form;
  serviceCategories: string[];
  serviceLogo: File | null;
  cv: File | null;
  guestPref: Pref;
  ownerPref: Pref;
  locations: string[];
  serviceLocations: ServiceLocation[];
  selectedPlan: PlanId | null;
  /** Vessel internal_email, already checked for conflicts (see useVesselEmailConflict). */
  vesselEmail: string;
  // job-seeker crew profile
  positions: string[];
  workTypes: WorkType[];
  availableFrom: AvailableFrom | null;
  /** The captain an owner invites, or the owner a captain invites, on the vessel path. */
  invite: { contact: string; role: 'captain' | 'owner' } | null;
}

/** How far from today each "Available from" option starts. "In 1–3 months" is the start of
    that range; "Later this year" picks up where it ends. */
const AVAILABLE_FROM_SHIFT: Record<AvailableFrom, (today: Date) => Date> = {
  now: (today) => today,
  '2w': (today) => addDays(today, 14),
  '1m': (today) => addMonths(today, 1),
  '3m': (today) => addMonths(today, 1),
  later: (today) => addMonths(today, 3),
};

/**
 * The day an "Available from" option names, as the backend's `date` column takes it: the
 * user's local calendar day at midnight UTC. Converting local midnight to UTC instead would
 * store the day before for anyone east of Greenwich.
 */
export const toAvailableFromDate = (option: AvailableFrom, today = new Date()): string =>
  `${format(AVAILABLE_FROM_SHIFT[option](today), 'yyyy-MM-dd')}T00:00:00Z`;

/** Wizard service-type chip id → real `service_category` enum value (verified). */
const SERVICE_CATEGORY_MAP: Record<string, string> = {
  hull: 'hull_exterior',
  wash: 'exterior_wash_detail',
  interior: 'interior_cleaning',
  mechanical: 'mechanical_electrical',
  electronics: 'electronics_navigation_communication',
  safety: 'safety_inspection',
  concierge: 'operational_concierge',
  yachtmgmt: 'yacht_management',
  upholstery: 'upholstery_canvas',
  crew: 'crew_services',
  agent: 'agent_services',
  emergency: 'emergency_services',
  other: 'other',
  // ponytail: 'marina' is NOT a category — it's a POST /{id}/marina sub-resource
  // needing max_length/max_beam/draft the wizard never collects. Dropped here.
};

/** Pref chips store a literal 'Other' sentinel; swap it for the free-text value. */
const withOther = (list: string[], other: string): string[] => [
  ...list.filter((x) => x !== 'Other'),
  ...(other.trim() ? [other.trim()] : []),
];

const toPreferences = (pref: Pref): PersonPreferences => ({
  favorite_drinks: withOther(pref.beverages, pref.beverageOther),
  favorite_foods: withOther(pref.food, pref.foodOther),
  dietary_restrictions: withOther(pref.dietary, pref.dietaryOther),
  room_preferences: [],
  seasickness_notes: null,
  // allergies is a single string on the backend, not an array.
  allergies: withOther(pref.allergies, pref.allergyOther).join(', '),
});

const toVesselPayload = (data: OnboardingData, lengthUnit: LengthUnit): CreateVesselPayload => {
  const raw = parseFloat(data.form.vesselLength);
  // length_overall is stored as integer feet; convert from the user's preferred unit.
  const lengthOverall = Number.isNaN(raw) ? undefined : Math.round(toCanonicalFt(raw, lengthUnit));

  return {
    vessel_name: data.form.vesselName.trim(),
    make: data.form.vesselMake || undefined,
    model: data.form.vesselModel || undefined,
    length_overall: lengthOverall,
    crew: [], // crew isn't part of vessel create; the crewInfo step is informational only
    // internal_email is unique backend-side. The wizard has no field for it, so it's derived
    // from the vessel name (same rule as CreateVesselSheet) and cleared of conflicts on the
    // vessel step; the fallback only matters if that check never ran.
    internal_email: data.vesselEmail || generateEmailFromVesselName(data.form.vesselName),
    created_as: data.role ?? 'owner',
    // The wizard has no timezone step; seed from the browser so the vessel isn't stuck on the
    // backend's 'UTC' default. Editable afterwards in the vessel form.
    timezone: getBrowserTimeZone(),
  };
};

const toServiceProviderValues = (data: OnboardingData): CreateUpdateServiceProvidersFormValues => ({
  name: data.form.businessName.trim(),
  short_description: null,
  description: data.form.serviceDescription,
  contact_info: {
    phone: data.form.contactPhone,
    email: data.form.contactEmail,
    website: ensureUrlProtocol(data.form.contactWebsite),
  },
  created_by: ServiceProviderCreatedBy.SERVICE_PROVIDER,
  service_category: data.serviceCategories.map((id) => SERVICE_CATEGORY_MAP[id]).filter(Boolean),
  // First geocoded location goes in with create (backend seeds the first location record).
  // Extra rows are POSTed to /:id/locations afterwards. Omitted when nothing geocoded.
  ...(data.serviceLocations[0] && {
    location: { ...data.serviceLocations[0], map_url: 'https://example.com/' },
  }),
});

/** Real signed-in identity, replacing the hard-coded USER mock. */
export function useOnboardingIdentity() {
  const currentUser = usePerson((s) => s.currentUser);
  const me = usePerson((s) => s.me);

  useEffect(() => {
    if (!currentUser) me();
  }, [currentUser, me]);

  return {
    personId: currentUser?.id ?? '',
    firstName: currentUser?.person_info.first_name ?? '',
    lastName: currentUser?.person_info.last_name ?? '',
    email: currentUser?.contacts?.find((c) => c.contact_type === 'email')?.contact ?? '',
  };
}

/**
 * Guards the vessel's derived `internal_email` against the backend's uniqueness rule.
 *
 * The address is built from the vessel name, so two "Serenity"s collide — and so does a second
 * vessel created by the same captain or owner. Left unchecked, create fails at the very end of
 * the wizard with a raw backend error. Call `ensureAvailable` when leaving the vessel step: it
 * returns false and opens the conflict modal when the address is taken, mirroring
 * CreateVesselSheet. `resolve` re-checks whatever the user typed there.
 */
export function useVesselEmailConflict() {
  const checkVesselEmailExists = useVessels((s) => s.checkVesselEmailExists);

  const [email, setEmail] = useState(''); // resolved address, free when it was checked
  const [conflictEmail, setConflictEmail] = useState(''); // the taken one, shown in the modal
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  // The name the resolved address was approved for, so stepping back and forward doesn't re-ask.
  const approvedFor = useRef<string | null>(null);

  const isFree = async (candidate: string): Promise<boolean> => {
    setChecking(true);
    try {
      return !(await checkVesselEmailExists(candidate));
    } catch (e) {
      // The check is advisory — a failed lookup must not trap the user on the step. A genuinely
      // taken address still surfaces as a create error on the final step.
      console.error('Vessel email check failed:', e);
      return true;
    } finally {
      setChecking(false);
    }
  };

  /** True when the wizard may advance; false when the conflict modal has taken over. */
  const ensureAvailable = async (vesselName: string): Promise<boolean> => {
    const name = vesselName.trim();
    if (approvedFor.current === name && email) return true;

    const candidate = generateEmailFromVesselName(name);
    if (!(await isFree(candidate))) {
      setConflictEmail(candidate);
      setOpen(true);
      return false;
    }
    setEmail(candidate);
    approvedFor.current = name;
    return true;
  };

  /** Modal "Continue" — keeps the modal open (showing the new value) while it stays taken. */
  const resolve = async (edited: string, vesselName: string): Promise<boolean> => {
    if (!(await isFree(edited))) {
      setConflictEmail(edited);
      return false;
    }
    setEmail(edited);
    approvedFor.current = vesselName.trim();
    setOpen(false);
    return true;
  };

  return {
    email,
    conflictEmail,
    open,
    checking,
    close: () => setOpen(false),
    ensureAvailable,
    resolve,
  };
}

/** Persists the wizard's collected data per intent, and navigates to the dashboard. */
export function useOnboardingSubmit() {
  const createVessel = useVessels((s) => s.createVessel);
  const currentUser = usePerson((s) => s.currentUser);
  const changePreference = usePerson((s) => s.changePreference);
  const completeOnboarding = useAuth((s) => s.completeOnboarding);
  const inviteUser = useAuth((s) => s.inviteUser);
  const refreshSession = useAuth((s) => s.me);
  const createServiceProvider = useCreateServiceProvider();
  const createMarina = useMarinaServicesStore((s) => s.createMarina);
  const navigate = useNavigate();
  const { lengthUnit } = useUnitPreferences();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (data: OnboardingData): Promise<boolean> => {
    setSubmitting(true);
    setError(null);
    try {
      switch (data.intent) {
        case 'vessel': {
          const vessel = await createVessel(toVesselPayload(data, lengthUnit));
          if (data.invite) {
            try {
              await inviteUser(
                data.invite.contact,
                data.invite.contact,
                data.invite.role,
                vessel.id,
                'vessel',
                vessel.vessel_name,
                data.invite.role,
              );
            } catch (inviteError) {
              console.error('Failed to send the onboarding invite:', inviteError);
              toast.error(`Couldn’t invite ${data.invite.contact}. Please try again from Crew.`);
            }
          }
          // Owner's own prefs (only collected when a chef is on the crew) → person preferences.
          if (currentUser) await changePreference(currentUser.id, toPreferences(data.ownerPref));
          break;
        }
        case 'services': {
          const provider = await createServiceProvider.mutateAsync({
            // ponytail: a standalone provider has no vessel — vessel_id falls back to the
            // active service id (often empty on first run). Confirm the backend contract.
            vesselId: getServiceId(),
            values: toServiceProviderValues(data),
            logo: data.serviceLogo, // the mutation PATCHes /{id}/logo after create when present
          });
          // Extra locations (beyond the first, which create seeded) → POST /{id}/locations one
          // by one. Role-gated on X-Service-Id (caller must be the SP creator), so set it first.
          // Best-effort per row: a failed location never traps the user on the finish step.
          const extraLocations = data.serviceLocations.slice(1);
          if (extraLocations.length || data.serviceCategories.includes('marina')) {
            setServiceId(provider.id);
          }
          if (extraLocations.length) {
            const service_category = data.serviceCategories
              .map((id) => SERVICE_CATEGORY_MAP[id])
              .filter(Boolean);
            const results = await Promise.allSettled(
              extraLocations.map((location) =>
                addServiceProviderLocation(provider.id, { location, service_category }),
              ),
            );
            results
              .filter((r) => r.status === 'rejected')
              .forEach((r) => console.error('Failed to add service-provider location:', r.reason));
          }
          // Marina chip → create the marina sub-resource (POST /{id}/marina), mirroring the
          // Add-Service-Provider form. Best-effort: createMarina swallows its own errors, so a
          // failed berth-detail save never traps the user on the finish step.
          if (data.serviceCategories.includes('marina')) {
            await createMarina(provider.id, {
              max_length: Math.round(
                toCanonicalFt(Number(data.form.marinaMaxLength || 0), lengthUnit),
              ),
              max_beam: Math.round(toCanonicalFt(Number(data.form.marinaMaxBeam || 0), lengthUnit)),
              shallowest_draft: Math.round(
                toCanonicalFt(Number(data.form.marinaDraft || 0), lengthUnit),
              ),
              price_per_foot_daily: `${data.form.marinaPriceDaily || 0}USD`,
              price_per_foot_monthly: `${data.form.marinaPriceMonthly || 0}USD`,
            });
          }
          break;
        }
        case 'guest':
          if (currentUser) {
            try {
              await changePreference(currentUser.id, toPreferences(data.guestPref));
            } catch (error) {
              console.error('Failed to save guest preferences:', error);
              toast.error('Couldn’t save your preferences. Please use My Profile.');
            }
          }
          break;
        case 'job': {
          // A profile must exist before a CV can attach (PATCH /profiles/cv 404s otherwise).
          // Seeded from the signed-in identity: the step asks only for the role, experience
          // and availability, so whichever of email and phone the account lacks is sent as
          // null (the mapper turns '' into null) and added later on the profile.
          if (currentUser) {
            const profile = generateCreateCrewProfile(currentUser);
            profile.positions = data.positions;
            profile.phoneNumber =
              currentUser.contacts?.find(({ contact_type }) => contact_type === 'phone')?.contact ??
              '';
            profile.yearsOfExperience = data.form.yearsExp ? Number(data.form.yearsExp) : null;
            profile.workTypes = data.workTypes;
            profile.availableFrom = data.availableFrom
              ? toAvailableFromDate(data.availableFrom)
              : null;
            await createProfile(profile);
            if (data.cv) await uploadCv(data.cv);
          }
          break;
        }
      }
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Couldn’t save your setup. Please try again.');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  // Single exit for finish, skip and the finish screen's next steps — flips the persisted
  // `onboarded` flag (and the session mirror the Layout gate reads) so the wizard never
  // reappears; navigating anywhere before that would bounce straight back here.
  // Swallow + log on failure so the user isn't trapped on the finish screen.
  const leaveOnboarding = async (to?: string) => {
    try {
      await completeOnboarding();
      // Onboarding just assigned a new role/service (e.g. service-provider) server-side.
      // Refetch /me so the auth session's roles + active service are current before we
      // land — otherwise the dashboard renders the stale (default) role until a refresh.
      await refreshSession();
    } catch (e) {
      console.error('Failed to finalize onboarding:', e);
    }
    // A user who arrived from an invite link (e.g. /event-preview) registered only to
    // get back to it — onboarding is the last leg of that trip, so it hands them over.
    navigate(to ?? consumeRedirectAfterAuth() ?? '/dashboard');
  };

  const goToDashboard = () => leaveOnboarding();

  return { submit, submitting, error, goToDashboard, leaveOnboarding };
}
